import React, { useCallback, useRef, useState } from 'react';
import { useReveal } from '../shell';
import { channels, profile } from '../data';

/**
 * Contact.
 *
 * Posts to the same /api/send-email endpoint with the same payload shape
 * (firstName, lastName, email, subject, message) as the original site, so both
 * presentations deliver to the same inbox and neither needs its own backend.
 */

const EMPTY = { firstName: '', lastName: '', email: '', subject: '', message: '' };

const validate = (v) => {
  const e = {};
  if (!v.firstName.trim()) e.firstName = 'Required';
  if (!v.lastName.trim()) e.lastName = 'Required';
  if (!v.email.trim()) e.email = 'Required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) e.email = 'Invalid address';
  if (!v.subject.trim()) e.subject = 'Required';
  if (!v.message.trim()) e.message = 'Required';
  else if (v.message.trim().length < 10) e.message = 'Min 10 characters';
  return e;
};

const Field = ({ name, label, type = 'text', textarea, values, errors, onChange, autoComplete }) => (
  <div className="m-field">
    <label htmlFor={`m-${name}`}>{label}</label>
    {textarea ? (
      <textarea
        id={`m-${name}`}
        name={name}
        value={values[name]}
        onChange={onChange}
        aria-invalid={Boolean(errors[name])}
        aria-describedby={errors[name] ? `m-${name}-err` : undefined}
      />
    ) : (
      <input
        id={`m-${name}`}
        name={name}
        type={type}
        autoComplete={autoComplete}
        value={values[name]}
        onChange={onChange}
        aria-invalid={Boolean(errors[name])}
        aria-describedby={errors[name] ? `m-${name}-err` : undefined}
      />
    )}
    {errors[name] && (
      <span className="m-field__error" id={`m-${name}-err`}>
        {errors[name]}
      </span>
    )}
  </div>
);

const Contact = () => {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const statusRef = useRef(null);

  useReveal([]);

  const onChange = useCallback((event) => {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  }, []);

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const found = validate(values);
      setErrors(found);
      if (Object.keys(found).length > 0) {
        document.querySelector('[aria-invalid="true"]')?.focus();
        return;
      }

      setStatus('sending');
      try {
        const res = await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        });
        if (!res.ok) throw new Error(`send failed: ${res.status}`);
        setStatus('sent');
        setValues(EMPTY);
      } catch (error) {
        console.error('[modernist] contact submit failed:', error);
        setStatus('error');
      }
      statusRef.current?.focus();
    },
    [values]
  );

  return (
    <>
      <section className="m-head">
        <div className="m-head__kicker">Contact</div>
        <h1 className="m-head__title">
          Say
          <br />
          something
        </h1>
        <p className="m-head__lead">
          It lands at {profile.email}. Replies are measured in hours, not weeks.
        </p>
      </section>

      <section className="m-contact">
        <div className="m-contact__cell">
          <form onSubmit={onSubmit} noValidate>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <Field
                name="firstName"
                label="First name"
                autoComplete="given-name"
                values={values}
                errors={errors}
                onChange={onChange}
              />
              <Field
                name="lastName"
                label="Last name"
                autoComplete="family-name"
                values={values}
                errors={errors}
                onChange={onChange}
              />
            </div>
            <Field
              name="email"
              label="Email"
              type="email"
              autoComplete="email"
              values={values}
              errors={errors}
              onChange={onChange}
            />
            <Field name="subject" label="Subject" values={values} errors={errors} onChange={onChange} />
            <Field name="message" label="Message" textarea values={values} errors={errors} onChange={onChange} />

            <button type="submit" className="m-btn m-btn--primary" disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending…' : 'Send message'}
            </button>

            <p
              ref={statusRef}
              tabIndex={-1}
              role="status"
              aria-live="polite"
              style={{
                marginTop: 16,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--color-accent)',
              }}
            >
              {status === 'sent' && 'Sent — a confirmation is on its way to you.'}
              {status === 'error' && 'That did not go through. Use a direct channel instead.'}
            </p>
          </form>
        </div>

        <div className="m-contact__cell m-contact__cell--last">
          <div className="m-split__kicker">Direct</div>
          <h2 className="m-split__title">Channels</h2>
          <div className="m-channels">
            {channels.map((c) => (
              <div key={c.label} className="m-channel">
                <span className="m-channel__label">{c.label}</span>
                {c.href ? (
                  <a
                    href={c.href}
                    target={c.href.startsWith('http') ? '_blank' : undefined}
                    rel={c.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  >
                    {c.value}
                  </a>
                ) : (
                  <span>{c.value}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
